/**
 * Bonus deployment engine — allocations derived from state, not from literals.
 *
 * WHY THIS EXISTS
 * The previous planner (bonus.js _bonusArchitecture) advertised itself as a
 * "deterministic, state-aware deployment planner" while its tranche set, tranche order
 * and percentage splits were hardcoded: 0.60/0.28/0.12, 0.63/0.27/0.10, 0.50/0.30/0.20.
 * Market conditions could not resize a single tranche. Fear & Greed toggled one string
 * of copy. The instrument label was a hardcoded ticker whitelist intersected with
 * S.holdings in ARRAY ORDER, so it emitted the same three names forever and could never
 * name the pending UCITS lines. (audit 2026-08-01)
 *
 * THE TWO-LEVEL TARGET MODEL
 * The repo carries two unreconciled target models and nothing reconciles them:
 *   Model A — S.rebalancing.targets category bands, surfaced by calcDrift(). Knows that
 *             "US ETF is 11.6pp below band". Knows nothing about instruments.
 *   Model B — per-holding h.allocationPct. Knows that CSPX.L has an 8% target. This is
 *             also where the pending buys live, encoded as qty === 0 with a target set.
 * Neither alone can answer "how much, into what". So: A SIZES THE CATEGORY, B PICKS THE
 * INSTRUMENT INSIDE IT. No schema change, no migration, reversible.
 *
 * HARD CONSTRAINTS
 *  - Pure. No state writes, no network, no LLM, no imports. ctx is injected, exactly like
 *    core/alpha-score.js, so this slots into the deterministic Netlify test gate.
 *  - Every tranche carries `drivers`: the signals that produced its size, with their
 *    values. If a card cannot say why it is recommending an amount, the engine is wrong.
 *  - Stale inputs cannot silently contribute. An AI-derived tranche whose evidence is
 *    past its freshness budget is zeroed and the reason is pushed to `warnings`.
 */

export const PROFILES = ['conservative', 'balanced', 'risky'];
export const LENSES = ['auto', 'portfolio', 'trade', 'fi'];

/** Risk-appetite coefficient. Scales every variance-bearing tranche. */
const APPETITE_K = { conservative: 0.55, balanced: 1.0, risky: 1.45 };

/** Composite-risk damping: a portfolio already carrying risk gets less new risk. */
const RISK_DAMP = { healthy: 1.0, elevated: 0.85, high: 0.6 };

/** AI evidence older than this contributes nothing. Matches ai-recommend.js. */
export const IMPACT_FRESH_MS = 6 * 60 * 60 * 1000;

/** Tranches smaller than this share of the pool are noise, not a decision. */
const MIN_TRANCHE_SHARE = 0.02;

/** ...and never smaller than this share of the POOL, however little is left to deploy. */
const MIN_TRANCHE_ABS_SHARE = 0.005;

/** The tranches that claim to close a measurable gap, and must therefore be bounded by it. */
const CAPPED_IDS = ['fill', 'dca', 'conviction'];

const clamp01 = n => (!isFinite(n) ? 0 : n < 0 ? 0 : n > 1 ? 1 : n);
const num = n => (typeof n === 'number' && isFinite(n) ? n : 0);

/**
 * Planned buys: a target weight with nothing bought yet.
 *
 * The codebase has no planned-buy concept; the 11 pending UCITS lines are encoded as
 * qty === 0 with allocationPct set, and no code path recognises the pattern. This is a
 * derived selector, so nothing is persisted and nothing migrates.
 */
export function plannedBuys(holdings) {
  return (holdings || [])
    .filter(h => h && h.ticker && !h._del && !(h.qty > 0) && num(h.allocationPct) > 0)
    .sort((a, b) => num(b.allocationPct) - num(a.allocationPct));
}

/**
 * Signals, each normalised to 0..1 so the weights below stay readable.
 * Exported for tests: every one of these must move an allocation.
 */
export function deriveSignals(ctx) {
  const c = ctx || {};
  const drifts = (c.drifts || []).filter(d => d && num(d.driftPP) < 0);
  const worstDrift = drifts.length ? Math.min(...drifts.map(d => num(d.driftPP))) : 0;
  const losers = (c.deepLosers || []).filter(x => x && num(x.pnlP) < 0);
  const worstLoss = losers.length ? Math.min(...losers.map(x => num(x.pnlP))) : 0;

  const fgVal = c.fg && c.fg.v != null ? num(c.fg.v) : null;
  // Fear & Greed is the macro dial: 0 = extreme fear (deploy hard), 50+ = no discount.
  const fear = fgVal == null ? 0.35 : clamp01((50 - fgVal) / 50);

  // 7-day direction. Deepening fear is a stronger buy signal than static fear.
  const hist = (c.fgHistory || []).map(h => num(h && (h.value != null ? h.value : h))).filter(v => v > 0);
  const fearTrend = (fgVal != null && hist.length >= 2) ? clamp01((hist[hist.length - 1] - fgVal) / 25) : 0;

  const impactsFresh = c.impactAgeMs == null ? false : c.impactAgeMs <= IMPACT_FRESH_MS;
  const convs = impactsFresh ? (c.conviction || []).map(x => clamp01(num(x && x.conf))) : [];
  const conviction = convs.length ? convs.reduce((a, b) => a + b, 0) / convs.length : 0;

  return {
    // 15pp below band is a full-strength underweight signal.
    drift: clamp01(-worstDrift / 15),
    worstDriftPP: worstDrift,
    // -45% is a full-strength averaging-down signal.
    drawdown: clamp01(-worstLoss / 45),
    worstLossPct: worstLoss,
    fear, fearTrend, fgValue: fgVal,
    conviction, impactsFresh,
    riskDamp: RISK_DAMP[String(c.riskBand || 'healthy').toLowerCase()] ?? 1.0,
    plannedCount: (c.plannedBuys || []).length,
  };
}

/** Lens multipliers. The lens re-tilts an already state-derived plan; it does not replace it. */
const LENS_TILT = {
  auto: { fill: 1, dca: 1, conviction: 1, fi: 1, powder: 1 },
  portfolio: { fill: 1.8, dca: 0.8, conviction: 0.6, fi: 0.6, powder: 0.8 },
  trade: { fill: 0.6, dca: 1.4, conviction: 2.0, fi: 0.6, powder: 0.8 },
  fi: { fill: 0.7, dca: 0.5, conviction: 0.4, fi: 2.4, powder: 0.6 },
};

/**
 * Pick the instruments for a category tranche — Model B inside Model A.
 * Planned buys (a target with nothing bought) outrank held names, because an unfunded
 * target is by definition the largest gap in that category.
 */
export function pickInstruments(category, ctx, limit = 3) {
  const c = ctx || {};
  const catOf = typeof c.categoryOf === 'function' ? c.categoryOf : () => null;
  const inCat = h => !category || catOf(h) === category;

  const planned = (c.plannedBuys || []).filter(inCat);
  if (planned.length) {
    return planned.slice(0, limit).map(h => ({ ticker: h.ticker, targetPct: num(h.allocationPct), planned: true }));
  }
  const held = (c.heldTargets || [])
    .filter(h => h && h.ticker && inCat(h))
    .slice()
    // Largest target-vs-actual shortfall first. Falls back to target size when actuals
    // are unavailable, which still beats the array order the old whitelist used.
    .sort((a, b) => (num(b.targetPct) - num(b.actualPct)) - (num(a.targetPct) - num(a.actualPct)));
  return held.slice(0, limit).map(h => ({ ticker: h.ticker, targetPct: num(h.targetPct), planned: false }));
}

/**
 * Produce the deployment plan.
 *
 * @returns {{tranches:Array, inputs:Object, warnings:Array<string>}}
 *   tranche = { id, kind, label, instruments[], amount, pct, score, drivers[], stale }
 */
export function planDeployment(ctx, opts = {}) {
  const c = ctx || {};
  const o = opts || {};   // an explicit null must behave like an omitted argument
  const pool = num(c.pool);
  const profile = PROFILES.includes(o.profile) ? o.profile : 'balanced';
  const lens = LENSES.includes(o.lens) ? o.lens : 'auto';
  const warnings = [];
  // The drawdown signal must come from the SAME positions the average-down leg may act on.
  // Round 3 measured a leg of TRY 266,713 whose card read "Worst drawdown -29.3%" beside an
  // instrument list containing only a name down 5%: the excluded position was still sizing it.
  const _sizingPre = c.sizing || { bySizing: {}, undeclared: [] };
  const _weighted = pid => (pid && _sizingPre.bySizing ? _sizingPre.bySizing[pid] : null) === 'weighted';
  const s = deriveSignals({ ...c, deepLosers: (c.deepLosers || []).filter(x => x && _weighted(x.portfolioId)) });

  if (pool <= 0) return { tranches: [], inputs: { ...s, pool: 0, profile, lens }, warnings: ['No net amount to deploy.'] };

  const k = APPETITE_K[profile];
  const tilt = LENS_TILT[lens];
  const tranches = [];

  // ── 1. Reserve. A hard first claim, and the only tranche that is not scored:
  //    a funding gap is not a preference to be weighed against a preference.
  const now = c.reserveNowTRY, tgt = c.reserveTargetTRY;
  let reserved = 0;
  if (now == null || tgt == null) {
    warnings.push('Reserve coverage or target is unavailable (no usable FX rate) — the reserve tranche was skipped, not sized at zero.');
  } else if (tgt > now) {
    // Risky funds only a half floor and deploys the rest; the others close the gap.
    const want = profile === 'risky' ? Math.max(0, tgt * 0.5 - now) : tgt - now;
    reserved = Math.min(pool, want);
    if (reserved > 0) {
      tranches.push({
        id: 'reserve', kind: 'cash', label: 'Reserve — fund the safety floor',
        instruments: [], amount: Math.round(reserved), score: null, stale: false,
        drivers: [
          { key: 'reserveGap', value: Math.round(tgt - now), contribution: 'sets the amount' },
          { key: 'profile', value: profile, contribution: profile === 'risky' ? 'half floor only' : 'closes the gap in full' },
        ],
      });
    }
  }

  // ── 1a. A sleeve that never declared how it is sized cannot be silently included. Saying so
  //    is the whole point: an unreported guess is what let a drawdown-ranked crypto tranche
  //    contradict an approved plan for three weeks. (ABSENCE RULE)
  const sizingIx = c.sizing || { bySizing: {}, undeclared: [] };
  (sizingIx.undeclared || []).forEach(p => {
    warnings.push(`Portfolio "${p.name}" has no sizing declared — it is excluded from this plan rather than assumed.`);
  });
  const sizingOf = pid => (pid && sizingIx.bySizing ? sizingIx.bySizing[pid] : null) || null;
  // ⚠ The predicate below is the SAME RULE as core/portfolio-policy.js `isDeployable`. It is
  //   spelled out here rather than imported because this module imports nothing by contract,
  //   and `a-sizing-is-not-a-mandate.test.js` pins the two to agree. Round 1 caught them disagreeing:
  //   the warning claimed exclusion while an undeclared sleeve, and any holding with no
  //   portfolioId at all, sailed straight into the drawdown tranche.
  const deployable_ = h => {
    const sz = sizingOf(h && h.portfolioId);
    return sz === 'weighted' || sz === 'cyclical';
  };

  // ── 1a-ter. Positions excluded from the average-down leg, and a breached invalidation.
  //    Both used to live below the `deployable <= 0` early return, so they went silent exactly
  //    when the plan was most constrained; and the invalidation only reached the screen through
  //    `alloc`, which does not exist outside a plan month -- "your existing bags are on stop" is
  //    a fact about positions held, not about whether this month has a buy leg. (round 4)
  const _allLosers = (c.deepLosers || []).filter(x => x && num(x.pnlP) < 0);
  const _orphans = _allLosers.filter(x => !deployable_(x) && sizingOf(x.portfolioId) !== 'accumulate' && sizingOf(x.portfolioId) !== 'cyclical');
  if (_orphans.length) {
    warnings.push(`${_orphans.length} losing position(s) (${_orphans.map(x => x.ticker).join(', ')}) sit in no sleeve with a declared sizing — they are excluded from the average-down leg.`);
  }
  // The WHOLE plan's coin list, not this month's leg: BTC is named by the plan in every month,
  // and building this from the month made the warning fire on BTC itself from March 2027 on.
  const _planned = new Set((c.cycle && Array.isArray(c.cycle.allPlanTickers)) ? c.cycle.allPlanTickers : []);
  const _betweenLegs = _planned.size
    ? _allLosers.filter(x => sizingOf(x.portfolioId) === 'cyclical' && !_planned.has(x.ticker))
    : [];
  if (_betweenLegs.length) {
    warnings.push(`${_betweenLegs.map(x => x.ticker).join(', ')} ${_betweenLegs.length > 1 ? 'are' : 'is'} in a cyclical sleeve but not named by the cycle plan — covered by neither leg.`);
  }
  const _inval = (c.cycle && c.cycle.gates && Array.isArray(c.cycle.gates.invalidated)) ? c.cycle.gates.invalidated : [];
  if (_inval.length) {
    warnings.push(`Cycle invalidation breached: ${_inval.map(i => `${i.ticker} at ${i.close} is below ${i.level}`).join('; ')}. Existing positions are on stop, regardless of whether this month has a buy leg.`);
  }

  // ── 1a-bis. Top-level band positions. `calcDrift` has always produced these and the engine
  //    has never seen them, which is the omission this whole change exists to close. A sleeve
  //    at or past its ceiling is a fact the plan must state even when it cannot act on it --
  //    an `accumulate` sleeve is exactly the case where the app may report but not propose.
  if (c.driftsAvailable === false) {
    warnings.push('The rebalancing drift model is unavailable (no targets or no FX rate) — nothing was measured against a band, so there is no underweight leg in this plan at all.');
  }
  (c.cycle && Array.isArray(c.cycle.discrepancies) ? c.cycle.discrepancies : []).forEach(d => {
    warnings.push(`The approved plan's ${d.phase} states $${d.statedUsd} but its own per-coin split sums to $${d.splitSum} (${d.delta > 0 ? '+' : ''}${d.delta}). The split is used; the record disagrees with itself.`);
  });
  const tl = c.topLevel || null;
  if (tl) {
    Object.keys(tl).forEach(tlKey => {
      const a = tl[tlKey];
      if (!a || !a.band) return;
      // A band describes where a WEIGHTED sleeve should be steered. On an `accumulate` sleeve it
      // is a contradiction: the owner's instruction is to keep buying until the sleeve's own
      // price trigger fires, and nothing else may push back. Round 2 added this warning without
      // asking which sizing it applied to, and it landed on SAP XETRA -- the one sleeve that
      // must never be argued with. (2026-09-02)
      const _sz = a.sizing || sizingOf(a.id);
      // `cyclical` joined `accumulate` on 2026-09-03: the crypto ceiling was removed, so there
      // is no band left to report against. Reporting one anyway would be the plan arguing with
      // a decision it was told not to touch, which is the same defect this guard was written
      // for. What replaces the ceiling is the invalidation stop, graded by integrity.
      if (_sz === 'accumulate' || _sz === 'cyclical') return;
      // `units` is a share of the retirement sleeve, so it reads 0 for EVERY top-level sleeve
      // whenever that denominator is 0 — a price refresh that has not landed, for instance.
      // The producer already classifies this; trust its label rather than re-deriving one.
      if (a.status === 'empty') {
        if (a.band.lo > 0) warnings.push(`${a.name} is EMPTY and its band starts at ${a.band.lo}.`);
        return;
      }
      if (!(a.units > 0)) {
        warnings.push(`${a.name} could not be measured against its ${a.band.lo}-${a.band.hi} band (no comparable base).`);
        return;
      }
      const { lo, hi } = a.band;
      if (a.units > hi) warnings.push(`${a.name} is at ${a.units.toFixed(1)} units, ABOVE its ${lo}-${hi} band.`);
      else if (a.units >= hi - (hi - lo) * 0.1 && _sz !== 'cyclical') warnings.push(`${a.name} is at ${a.units.toFixed(1)} units and its band tops out at ${hi} — it is within a tenth of the band of its ceiling.`);
      // A cyclical sleeve sitting low during a hold phase is the plan working, not a gap to
      // fill, so only its CEILING is reported. A weighted sleeve is reported both ways.
      else if (a.units < lo && _sz !== 'cyclical') warnings.push(`${a.name} is at ${a.units.toFixed(1)} units, BELOW its ${lo}-${hi} band.`);
    });
  } else {
    warnings.push('Portfolio band positions are unavailable — no sleeve was checked against its ceiling.');
  }

  // ── 1b. Cycle claim. ABSOLUTE, from the approved plan, and a hard claim like the reserve —
  //    not a weight. Everything else in this engine is a normalised share of the pool, which is
  //    exactly why a large pool overshot every gap it claimed to close.
  let cycled = 0;
  const cyc = c.cycle || null;
  if (cyc) {
    const ph = cyc.phase;
    const al = cyc.alloc;
    if (!ph) {
      warnings.push('Cycle phase is unavailable — the crypto leg was skipped, not sized at zero.');
    } else if (!cyc.planMonth) {
      // Inside the halving window but not a month either phase names. That is a real state and
      // it is not an FX failure, which is what this branch used to say.
      warnings.push(`Cycle is in ${ph.phase} at H+${ph.h}, but ${cyc.month} is not a month the approved plan names — no leg.`);
    } else if (!al) {
      // The allocator owns the window rule INCLUDING the declared phase-2 tail. Re-deciding it
      // here from `phase !== 'accumulate'` deleted the whole of phase 2. (round 3, D1)
      warnings.push(`Cycle is in ${ph.phase} at H+${ph.h} — no crypto accumulation leg this month.`);
    } else if (al.stopped) {
      // Two different stops wore the same word. A failed price refresh was being announced as
      // "BTC broke its invalidation level and your existing bags are on stop". (round 3, D2)
      warnings.push(al.reason === 'gates_uncheckable'
        ? `The cycle gates could not be checked — no price for ${(al.unpriced || []).join(', ') || 'the plan coins'}. The leg is refused rather than sized on an unchecked entry rule.`
        : 'The crypto leg is stopped by the invalidation reported above.');
    } else if (!(cyc.claimTRY > 0)) {
      warnings.push('The cycle claim could not be converted to lira (no usable FX rate) — the leg was skipped, not zeroed.');
    } else if (!(pool - reserved > 0)) {
      warnings.push('The reserve claims the whole pool — the crypto leg was skipped, not shown at zero.');
    } else {
      const room = pool - reserved;
      cycled = Math.min(room, cyc.claimTRY);
      // A clipped claim IS "everything that is left", so it is rounded to exactly that. Leaving
      // a sub-lira residue here sent it back onto a hard claim through finalise. (round 3, D9)
      if (cycled >= room) cycled = Math.min(Math.round(room), Math.round(pool) - Math.round(reserved));
      if (cycled < cyc.claimTRY) {
        warnings.push(`The cycle leg wanted ${Math.round(cyc.claimTRY)} and only ${Math.round(cycled)} is left after the reserve — it is FUNDED SHORT, not re-planned.`);
      }
      const legs = Object.keys((cyc.alloc && cyc.alloc.split) || {});
      const drv = [
        { key: 'cyclePhase', value: `${ph.phase} H+${ph.h}`, contribution: 'sets the amount' },
        { key: 'planUSD', value: cyc.planUSD, contribution: cycled < cyc.claimTRY ? 'approved plan amount — THIS TRANCHE IS SHORT OF IT' : 'absolute, from the approved plan' },
        { key: 'confidence', value: ph.confidence, contribution: ph.confidence === 'projected_halving' ? 'projected halving' : 'observed halving' },
        { key: 'priceBasis', value: cyc.priceBasis || 'unknown', contribution: cyc.priceBasis === 'spot' ? 'gates checked on SPOT, the plan specifies a weekly close' : '' },
      ];
      if (cyc.alloc && cyc.alloc.xrpArmed === false) drv.push({ key: 'xrpGate', value: 'unconfirmed', contribution: 'XRP zeroed, redistributed 60/40 BTC/ETH' });
      if (cyc.alloc && cyc.alloc.held > 0) drv.push({ key: 'heldByGate', value: cyc.alloc.held, contribution: 'price-blocked leg held, not moved to another coin' });
      if (cyc.alloc && cyc.alloc.onTail) drv.push({ key: 'planTail', value: 'phase 2', contribution: 'declared tail — past the halving window on purpose' });
      const _cryptoBand = tl && Object.keys(tl).map(tlKey => tl[tlKey]).find(a => a && /crypto/i.test(String(a.name || '')));
      if (_cryptoBand && _cryptoBand.band) drv.push({ key: 'sleeveBand', value: `${_cryptoBand.units.toFixed(1)} of ${_cryptoBand.band.lo}-${_cryptoBand.band.hi}`, contribution: 'where the sleeve already sits' });
      tranches.push({
        id: 'cycle', kind: 'trades',
        // Instruments only; `_trancheDest` appends the tickers, and putting them here too
        // rendered "Cycle accumulation · BTC / ETH · BTC / ETH".
        label: legs.length ? 'Cycle accumulation' : 'Cycle accumulation — no leg passes the gates',
        instruments: legs.map(t => ({ ticker: t, usd: cyc.alloc.split[t] })),
        amount: Math.round(cycled), score: null, stale: false, drivers: drv,
      });
    }
  }

  const deployable = pool - reserved - cycled;
  if (deployable <= 0) {
    return { tranches: finalise(tranches, pool), inputs: { ...s, pool, profile, lens, reserved, cycled }, warnings };
  }

  // ── 2. Scored tranches. Every weight is a function of a signal; none is a literal split.
  // A cap is the size of the gap a tranche claims to close, in lira. `null` means the gap could
  // not be measured, and that is REPORTED rather than treated as "no limit".
  // BY TICKER, not by row. `valueTRY` is a per-ticker total, so a name appearing twice — two
  // holding rows for one position — used to be added to its own cap and the plan then asserted
  // that double was what the position is worth.
  const _sumCap = (rows) => {
    const seen = new Map();
    let missing = 0;
    (rows || []).forEach(r => {
      const tk = r && r.ticker;
      const v = num(r && r.valueTRY);
      if (!tk || !(v > 0)) { missing++; return; }
      seen.set(tk, v);
    });
    if (!seen.size) return null;
    if (missing) return null;
    return [...seen.values()].reduce((a, b) => a + b, 0);
  };
  if (c.capsAvailable === false) {
    warnings.push('No usable FX rate — no tranche could be bounded by the gap it claims to close, so every amount below is a weight, not a limit.');
  }

  const cand = [];

  // Underweight fill — Model A sizes it, Model B names the instruments.
  const worstCat = (c.drifts || []).filter(d => d && num(d.driftPP) < 0)
    .sort((a, b) => num(a.driftPP) - num(b.driftPP))[0] || null;
  const fillInstr = pickInstruments(worstCat && worstCat.category, c);
  // No category is below its band, so there is no underweight to fill. Funding an unbounded
  // "Portfolio underweights" leg here — with a card reading "Worst band drift is 0pp below
  // target" — was the cap's own blind spot.
  const _noBands = !(c.drifts || []).length;
  if (!worstCat && c.driftsAvailable !== false) {
    warnings.push(_noBands
      ? 'No category bands are configured, so nothing was checked against one — the underweight leg is not "satisfied", it was never measured.'
      : 'No category is below its band — there is no underweight to fill.');
  }
  // An unfunded target reaches a plan ONLY through the fill leg, and only through the fill leg
  // for ITS OWN category. Reporting the count regardless of category left eight of eleven
  // targets unfunded and unnamed on the live book; reporting nothing when the drift model was
  // unavailable dropped all of them in silence.
  if (worstCat) cand.push({
    id: 'fill', kind: 'portfolio',
    label: worstCat ? `Fill underweight · ${worstCat.category}` : 'Portfolio underweights',
    instruments: fillInstr,
    // THE CAP. A tranche that exists to close a gap may not exceed that gap. `calcDrift` has
    // always computed `actionUSD` per category and nothing ever bounded a recommendation with
    // it, so on a pool worth half the portfolio this leg proposed TRY 339,517 into a fund whose
    // real gap was about TRY 51,700 — six and a half times over. (2026-09-02)
    cap: (worstCat && num(worstCat.actionTRY) > 0) ? num(worstCat.actionTRY) : null,
    capWhy: worstCat ? `the ${worstCat.category} gap` : null,
    weight: (0.8 + 1.4 * s.drift + 0.5 * s.fear + (s.plannedCount ? 0.4 : 0)) * tilt.fill,
    drivers: [
      { key: 'driftPP', value: +s.worstDriftPP.toFixed(1), contribution: (1.4 * s.drift).toFixed(2) },
      { key: 'fearGreed', value: s.fgValue, contribution: (0.5 * s.fear).toFixed(2) },
      { key: 'plannedBuys', value: s.plannedCount, contribution: s.plannedCount ? '0.40' : '0.00' },
    ],
  });

  // Averaging into a deep drawdown. Variance-bearing: appetite and risk damping apply.
  // A cyclical sleeve is sized by its phase above, and an accumulate sleeve is not proposed
  // against at all. Leaving them here is what ranked a $326 XRP position first, on the sole
  // ground that it had fallen furthest.
  const losers = _allLosers.filter(x => sizingOf(x.portfolioId) === 'weighted')
    .sort((a, b) => num(a.pnlP) - num(b.pnlP));
  if (losers.length) {
    cand.push({
      id: 'dca', kind: 'trades',
      label: `Average down · ${losers.slice(0, 2).map(x => `${x.ticker} ${num(x.pnlP).toFixed(0)}%`).join(', ')}`,
      instruments: losers.slice(0, 3).map(x => ({ ticker: x.ticker, pnlPct: +num(x.pnlP).toFixed(1) })),
      // Adding more than the position is currently worth is no longer averaging down; it is a
      // new and larger bet wearing the old one's name.
      cap: _sumCap(losers.slice(0, 3)),
      capWhy: 'what those positions are worth today',
      weight: (0.3 + 1.5 * s.drawdown + 0.7 * s.fear + 0.5 * s.fearTrend) * k * s.riskDamp * tilt.dca,
      drivers: [
        { key: 'worstDrawdownPct', value: +s.worstLossPct.toFixed(1), contribution: (1.5 * s.drawdown).toFixed(2) },
        { key: 'fearGreed', value: s.fgValue, contribution: (0.7 * s.fear).toFixed(2) },
        { key: 'fearTrend7d', value: +s.fearTrend.toFixed(2), contribution: (0.5 * s.fearTrend).toFixed(2) },
        { key: 'appetite', value: profile, contribution: `x${k}` },
        { key: 'riskDamp', value: s.riskDamp, contribution: `x${s.riskDamp}` },
      ],
    });
  }

  // AI conviction. Zeroed outright when the evidence is stale — the previous planner read
  // S.aiCopilot.impacts with no freshness gate at all.
  // A bullish read on a name held only in an `accumulate` sleeve is not a place to add: that
  // sleeve's whole definition is that the app never proposes against it. `dca` has always
  // filtered on this; this leg never did, and round 4 removed its ROOM without removing the leg.
  const _convAll = (c.conviction || []);
  const _convBlocked = _convAll.filter(x => x && !deployable_(x));
  if (_convBlocked.length) {
    warnings.push(`${_convBlocked.map(x => x.ticker).join(', ')} came back bullish but ${_convBlocked.length > 1 ? 'sit' : 'sits'} in a sleeve this plan may not propose against — no conviction leg for ${_convBlocked.length > 1 ? 'them' : 'it'}.`);
  }
  const convNames = _convAll.filter(x => x && deployable_(x)).slice(0, 3);
  if (convNames.length) {
    if (!s.impactsFresh) {
      warnings.push('AI conviction evidence is stale or missing — that tranche was dropped rather than sized from old impacts.');
    } else {
      cand.push({
        id: 'conviction', kind: 'trades',
        label: `High-conviction adds · ${convNames.map(x => x.ticker).join(', ')}`,
        instruments: convNames.map(x => ({ ticker: x.ticker, conf: num(x.conf), why: x.why || '' })),
        cap: _sumCap(convNames),
        capWhy: 'what those positions are worth today',
        weight: (0.2 + 1.4 * s.conviction) * k * s.riskDamp * tilt.conviction,
        drivers: [
          { key: 'avgConfidence', value: +s.conviction.toFixed(2), contribution: (1.4 * s.conviction).toFixed(2) },
          { key: 'appetite', value: profile, contribution: `x${k}` },
        ],
      });
    }
  }

  // FI accelerator. Deliberately the steady leg: it does not chase the macro dial.
  cand.push({
    id: 'fi', kind: 'goals', label: 'FI accelerator → Net-Worth goal', instruments: [],
    weight: 0.9 * tilt.fi,
    drivers: [{ key: 'lens', value: lens, contribution: `x${tilt.fi}` }],
  });

  // Dry powder. The inverse of the others: more when there is no discount, more when the
  // appetite is low, more when the portfolio is already carrying risk.
  cand.push({
    id: 'powder', kind: 'cash', label: 'Dry powder · USD hedge', instruments: [],
    weight: (0.35 + 0.9 * (1 - s.fear) + (1 - s.riskDamp)) / k * tilt.powder,
    drivers: [
      { key: 'fearGreed', value: s.fgValue, contribution: (0.9 * (1 - s.fear)).toFixed(2) },
      { key: 'appetite', value: profile, contribution: `/${k}` },
      { key: 'riskDamp', value: s.riskDamp, contribution: (1 - s.riskDamp).toFixed(2) },
    ],
  });

  const totalW = cand.reduce((a, t) => a + Math.max(0, t.weight), 0);
  if (totalW <= 0) {
    warnings.push('No signal produced a positive weight — nothing to allocate beyond the reserve.');
    return { tranches: finalise(tranches, pool), inputs: { ...s, pool, profile, lens, reserved, cycled }, warnings };
  }

  let dropped = 0; const droppedIds = [];
  let capped = 0; const cappedNotes = [];
  // ── THE UNION. Each leg used to be checked against its OWN cap in isolation, and nothing
  //    looked at the overlap: CVX was simultaneously the deepest loser, the AI's bullish name
  //    and the only US Stock holding, so `fill` + `dca` + `conviction` proposed TRY 1,186,546
  //    into a position worth TRY 400,000. Three legs, each "inside its cap", 2.97x the asset.
  const committed = Object.create(null);
  // De-duplicated: an instrument list naming the same position twice must not double its room.
  // De-duplicated by NORMALISED key: two spellings of one position must not each get room.
  const tickersOf = (t) => {
    const seen = new Map();
    (t.instruments || []).forEach(i => {
      const raw = i && i.ticker;
      if (raw && !seen.has(normKey(raw))) seen.set(normKey(raw), raw);
    });
    return [...seen.values()];
  };
  const normKey = typeof c.normKey === 'function' ? c.normKey : (x => x);
  const valueOf = x => num(c.tickerValueTRY && c.tickerValueTRY[normKey(x)]);
  /** Per-ticker room left, for the names that HAVE a value. `unvalued` is reported, not zeroed. */
  const room = (t) => {
    const tk = tickersOf(t);
    const valued = tk.filter(x => valueOf(x) > 0);
    const unvalued = tk.filter(x => !(valueOf(x) > 0));
    const each = valued.map(x => ({ ticker: x, left: Math.max(0, valueOf(x) - num(committed[normKey(x)])) }));
    return { tk, valued, unvalued, each, total: each.reduce((a, b) => a + b.left, 0) };
  };
  // Spread PRO RATA by the room each name actually has. Splitting equally credited a name worth
  // 1,000 with 152,627 of the leg, freeing headroom on the big name that was never really free.
  const commit = (t, amount, r) => {
    if (!r || !r.each.length || !(r.total > 0)) return;
    r.each.forEach(e => { committed[normKey(e.ticker)] = num(committed[normKey(e.ticker)]) + amount * (e.left / r.total); });
  };

  // Largest first, so the union is spent in the order the weights actually ranked the legs.
  cand.sort((a, b) => Math.max(0, b.weight) - Math.max(0, a.weight));

  const tookFrom = Object.create(null);
  cand.forEach(t => {
    let amount = deployable * (Math.max(0, t.weight) / totalW);
    const own = t.cap;
    const r = room(t);
    // A leg carrying a name nobody can value is bounded by the names that CAN be valued, and the
    // unvalued one is named out loud. Voiding the whole bound because of one such name let three
    // legs put 2.4x a position's value into it while a warning said they could not be bounded.
    if (r.unvalued.length && r.valued.length) {
      warnings.push(`The ${t.id} leg names ${r.unvalued.join(', ')}, which cannot be valued right now — it is bounded by the rest and that name is not counted.`);
    }
    // `fill` is deliberately exempt: it must be able to exceed what the category holds, which is
    // the definition of filling an underweight. It still commits, so the later legs see less.
    const hr = (t.id === 'fill') ? null : (r.valued.length ? r.total : null);
    // The binding limit is whichever is smaller: the gap this leg claims to close, or what is
    // left of the assets it names after the legs above it took their share.
    const lim = (own != null && hr != null) ? Math.min(own, hr) : (own != null ? own : hr);

    let bit = false;
    if (lim != null && amount > lim) {
      bit = true;
      capped += amount - lim;
      const roomBound = hr != null && (own == null || hr < own);
      const takers = roomBound
        ? [...new Set(r.each.flatMap(e => tookFrom[normKey(e.ticker)] || []))]
        : [];
      const why = roomBound
        ? `what is left of ${r.valued.join(', ')}${takers.length ? ` after ${takers.join(', ')} took a share` : ''}`
        : t.capWhy;
      cappedNotes.push(`${t.id} was cut from ${Math.round(amount)} to ${Math.round(lim)} — ${why}`);
      amount = lim;
      t.drivers = (t.drivers || []).concat({ key: 'cap', value: `${Math.round(lim)} — ${why}`, contribution: '' });
    } else if (lim == null && CAPPED_IDS.includes(t.id)) {
      // Dropped, not funded. `amount` goes to the same held bucket as an over-cap cut, because
      // that is what it is: money no leg could justify wanting.
      capped += amount;
      cappedNotes.push(c.capsAvailable === false
        ? `${t.id} could not be bounded (no usable FX rate) and was dropped rather than funded from a weight`
        : `${t.id} names nothing this plan can measure — dropped rather than funded from a weight with no limit behind it`);
      return;
    }

    if (amount < deployable * MIN_TRANCHE_SHARE || amount < pool * MIN_TRANCHE_ABS_SHARE) {
      // Attribute it to whichever actually made it small. A leg that came out at 53,594 and was
      // CUT to 1,943 did not "come out too small"; the cap made it small, and saying both is the
      // conflation this was supposed to end.
      if (bit) {
        capped += amount;
        // Amend this leg's own note rather than adding a second one: "2 leg(s)" for one leg,
        // named twice, was 19.8% of every cap warning.
        const i = cappedNotes.findIndex(n => n.startsWith(`${t.id} was cut`));
        if (i >= 0) cappedNotes[i] += `, then fell below the floor and was dropped`;
        else cappedNotes.push(`${t.id} was cut to ${Math.round(amount)} and then fell below the floor`);
      }
      else { dropped += amount; droppedIds.push(t.id); }
      return;
    }
    // Filling a gap through a small line multiplies that line, and the amount alone does not
    // say so. 14pp of a category behind one holding worth TRY 21,843 is a 109x concentration
    // dressed as a band correction.
    if (t.id === 'fill' && r.tk.length) {
      // Against what the named positions are WORTH, not against what is left of them. And the
      // strongest form of the case is a leg aimed at a line that does not exist yet: a planned
      // buy has no value at all, so no multiple can be printed and the old test never fired.
      const worth = r.valued.reduce((a, x) => a + valueOf(x), 0);
      if (!(worth > 0)) {
        warnings.push(`This leg opens ${r.tk.join(', ')} from nothing — ${Math.round(amount)} into a line the book does not hold yet. That is a new position, not a top-up.`);
      } else if (amount > worth * 3) {
        warnings.push(`Filling this gap means multiplying ${r.valued.join(', ')} by about ${Math.round(amount / worth)}x — that is what the band asks for, and it is a concentration decision, not only a rebalance.`);
      }
    }
    commit(t, amount, r);
    // Record WHICH names this leg actually took room from, so a later cut can name the leg that
    // took it rather than every leg that happened to run first.
    r.each.forEach(e => {
      const share = (r.total > 0) ? amount * (e.left / r.total) : 0;
      if (share > 0) (tookFrom[normKey(e.ticker)] = tookFrom[normKey(e.ticker)] || []).push(t.id);
    });
    tranches.push({
      id: t.id, kind: t.kind, label: t.label, instruments: t.instruments,
      amount: Math.round(amount), score: +(t.weight / totalW).toFixed(4),
      drivers: t.drivers, stale: false,
      // F8: this flag says "the cap BIT", not "a cap exists". `finalise` refuses the rounding
      // residue to a capped tranche because its card shows a cap chip beside its amount, and
      // a tranche that was never cut shows no such chip.
      capped: bit,
    });
  });

  // Two different kinds of un-deployed money, and they used to share one bucket and one
  // sentence: TRY 820,509 of over-cap money was announced as "1 leg(s) (over-cap) came out too
  // small to be decisions", naming a leg that does not exist.
  if (capped >= 1) {
    warnings.push(`${cappedNotes.length} leg(s) proposed more than the gap they close: ${cappedNotes.join('; ')}. The difference is held, not moved to another leg — moving it would be a different decision than the one the weights produced.`);
  }
  if (dropped >= 1) {
    warnings.push(`${droppedIds.length} leg(s) (${droppedIds.join(', ')}) came out too small to be decisions and are held as dry powder rather than folded into another tranche.`);
  }
  const held = dropped + capped;
  if (held >= 1) {
    const drv = [];
    if (dropped >= 1) drv.push({ key: 'belowNoiseFloor', value: droppedIds.join(', '), contribution: 'legs too small to be decisions' });
    if (capped >= 1) drv.push({ key: 'overCap', value: Math.round(capped), contribution: 'proposed beyond the gap it closes, held instead' });
    const pw = tranches.find(t => t.id === 'powder');
    if (pw) { pw.amount += Math.round(held); pw.drivers = (pw.drivers || []).concat(drv); }
    else {
      tranches.push({
        id: 'powder', kind: 'cash', label: 'Dry powder · USD hedge', instruments: [],
        amount: Math.round(held), score: null, stale: false, drivers: drv,
      });
    }
  }

  // ── Unfunded targets, judged against the plan that ACTUALLY EXISTS.
  //    A pending line reaches a plan only through the underweight leg for its own category, so
  //    the question is whether that leg survived -- not what the candidate would have named. And
  //    the leg funds its whole CATEGORY; `pickInstruments` merely lists the first three.
  {
    const fillLeg = tranches.find(t => t.id === 'fill');
    const fundedCat = fillLeg && worstCat ? worstCat.category : null;
    const catOf = typeof c.categoryOf === 'function' ? c.categoryOf : () => null;
    const orphan = (c.plannedBuys || []).filter(h => {
      if (!h || !h.ticker) return false;
      if (!fundedCat) return true;
      let cat = null;
      try { cat = catOf(h); } catch (_e) { cat = null; }
      return cat !== fundedCat;
    });
    if (orphan.length) {
      warnings.push(`${orphan.length} target(s) with nothing bought yet (${orphan.slice(0, 4).map(h => h.ticker).join(', ')}${orphan.length > 4 ? ', …' : ''}) are not covered by this plan — a pending line reaches one only through the underweight leg for its own category, and ${fundedCat ? `this plan funds ${fundedCat}` : 'this plan has no underweight leg'}.`);
    }
  }

  return { tranches: finalise(tranches, pool), inputs: { ...s, pool, profile, lens, reserved, cycled }, warnings };
}

/**
 * Percentages, ordering, and an exact sum.
 * Rounding is absorbed by the largest tranche so the plan always adds up to the pool —
 * a deployment plan that does not sum to the money you have is not a plan.
 */
const HARD_CLAIMS = ['reserve', 'cycle'];

function finalise(tranches, pool) {
  if (!tranches.length) return tranches;
  tranches.sort((a, b) => b.amount - a.amount);
  const sum = tranches.reduce((s, t) => s + t.amount, 0);
  const drift = Math.round(pool) - sum;
  // The residue goes to the largest SCORED tranche, never to a hard claim. A reserve gap and a
  // cycle claim are both computed absolutely from something already decided; shifting either by
  // a lira to make the column add up would make "absolute" a claim the code does not honour.
  // Only if the plan is nothing but hard claims does the largest of them absorb it.
  if (drift !== 0) {
    // A CAPPED tranche is as absolute as a hard claim: it renders a `cap` chip beside its own
    // amount, and handing it the residue printed "TRY 560,001" next to "cap 560000".
    const soft = tranches.find(t => !HARD_CLAIMS.includes(t.id) && !t.capped)
      || tranches.find(t => !HARD_CLAIMS.includes(t.id));
    (soft || tranches[0]).amount += drift;
  }
  tranches.forEach(t => { t.pct = pool > 0 ? Math.round((t.amount / pool) * 100) : 0; });
  return tranches;
}
